// src/hooks/useSafetyCheck.ts
// Orchestrates all three safety layers:
//   Layer 1 — bad-words (client, instant)
//   Layer 2 — PII regex (client, instant)
//   Layer 3 — Claude AI via Cloud Function (async, ~1-2s)
//
// runPublishCheck  → all 3 layers, used at publish time
// runSaveCheck     → layers 1 & 2 only, used at save time (no API call)
 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Filter } from 'bad-words';
import { detectPII } from '../utils/piiDetector';
 
export type SafetyCategory = 'profanity' | 'pii' | 'bullying' | 'inappropriate' | 'contact';
 
export interface SafetyIssue {
  category: SafetyCategory;
  detail: string; // child-friendly explanation
}
 
export interface SafetyResult {
  /** false = hard block — fixable by the writer (layers 1 & 2) */
  passed: boolean;
  /** true = queued for teacher/human review (layer 3 AI flag) */
  needsReview: boolean;
  issues: SafetyIssue[];
}
 
// ── Shared bad-words filter instance ─────────────────────────────────────────
function buildFilter(): InstanceType<typeof Filter> {
  const f = new Filter();
  // Sports / common words falsely flagged
  f.removeWords('shooting', 'shoot', 'shot', 'shots', 'score', 'scored',
    'ball', 'balls', 'hard', 'harder', 'hardest', 'butt');
  f.addWords('damn', 'crap', 'hell', 'suck', 'sucks');
  return f;
}
 
// ── Text extractor — handles block-based and legacy chapter formats ────────────
function extractText(story: {
  title?: string;
  description?: string;
  content?: {
    chapters?: Array<{
      title?: string;
      content?: string;
      blocks?: Array<{ type: string; content?: string }>;
    }>;
  };
}): string {
  let text = `${story.title || ''} ${story.description || ''} `;
  story.content?.chapters?.forEach(ch => {
    text += `${ch.title || ''} `;
    ch.blocks?.forEach(b => {
      if (b.type === 'text' && b.content) {
        text += b.content.replace(/<[^>]*>/g, '') + ' ';
      }
    });
    if (ch.content) text += ch.content.replace(/<[^>]*>/g, '') + ' ';
  });
  console.log('Extracted text for safety check:', text);
  return text.trim();
}
 
// ── Layers 1 & 2 — used by both publish and save checks ──────────────────────
function runFastLayers(text: string): SafetyIssue[] {
  const issues: SafetyIssue[] = [];
  const filter = buildFilter();
 
  const words = text.toLowerCase().split(/\s+/);
  const flagged = Array.from(new Set(
    words
      .map(w => w.replace(/[^a-z0-9]/gi, ''))
      .filter(w => w && filter.isProfane(w))
  ));
 
  if (flagged.length > 0) {
    issues.push({
      category: 'profanity',
      detail: `Remove or replace these words: ${flagged.join(', ')}`,
    });
  }
 
  const pii = detectPII(text);
  pii.forEach(p => issues.push({ category: 'pii', detail: p.detail }));
 
  return issues;
}
 
// ── Main hook ─────────────────────────────────────────────────────────────────
export function useSafetyCheck() {
 
  /**
   * Full 3-layer check — call at publish time.
   * Pass isTeacher: true to bypass all checks.
   */
  const runPublishCheck = async (
    story: Parameters<typeof extractText>[0],
    isTeacher: boolean,
  ): Promise<SafetyResult> => {
    if (isTeacher) return { passed: true, needsReview: false, issues: [] };
 
    const text = extractText(story);
 
    // Layers 1 & 2
    const fastIssues = runFastLayers(text);
    if (fastIssues.length > 0) {
      return { passed: false, needsReview: false, issues: fastIssues };
    }
 
    // Layer 3 — AI check via Cloud Function
    try {
      const fn = httpsCallable<{ storyText: string }, { passed: boolean; issues: SafetyIssue[] }>(
        getFunctions(), 'runSafetyCheck'
      );
      const result = await fn({ storyText: text });
      const data = result.data;
 
      if (!data.passed && data.issues?.length > 0) {
        // AI flagged — queue for human review rather than hard-blocking
        return { passed: true, needsReview: true, issues: data.issues };
      }
    } catch (e) {
      // If the Cloud Function is unreachable, fail open
      console.warn('AI safety check unavailable, skipping:', e);
    }
 
    return { passed: true, needsReview: false, issues: [] };
  };
 
  /**
   * Lightweight check — layers 1 & 2 only, no API call.
   * Call at save time to show the inline warning.
   */
  const runSaveCheck = (story: Parameters<typeof extractText>[0]): SafetyIssue[] => {
    return runFastLayers(extractText(story));
  };
 
  return { runPublishCheck, runSaveCheck };
}